import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Construction, HardHat, Cog, Wrench, ArrowLeft } from "lucide-react";

const WorkInProgress = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf;
    const animate = () => {
      setProgress((p) => {
        // gentle oscillation between 35% and 72%
        const next = 53 + Math.sin(Date.now() / 1400) * 18;
        return next;
      });
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <main
      className="relative z-10 pt-24 min-h-screen flex items-center"
      data-testid="wip-page"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16">
        <div className="glass rounded-3xl border border-amber-400/20 overflow-hidden relative">
          {/* Construction tape */}
          <div className="absolute top-0 left-0 right-0 h-3 construction-stripes" />
          <div className="absolute bottom-0 left-0 right-0 h-3 construction-stripes" />

          <div className="p-8 sm:p-14 lg:p-20 text-center relative">
            {/* Animated gears cluster */}
            <div className="relative w-44 h-44 sm:w-56 sm:h-56 mx-auto mb-10">
              <span className="absolute inset-0 rounded-full bg-amber-400/20 pulse-ring" />
              <span
                className="absolute inset-0 rounded-full bg-amber-400/15 pulse-ring"
                style={{ animationDelay: "1.2s" }}
              />
              <div className="relative w-full h-full grid place-items-center">
                <Cog
                  className="absolute w-32 h-32 sm:w-40 sm:h-40 text-amber-400/90 rotate-gear"
                  strokeWidth={1.5}
                />
                <Cog
                  className="absolute w-16 h-16 sm:w-20 sm:h-20 text-amber-300 rotate-gear-reverse -bottom-2 -right-2 sm:-bottom-3 sm:-right-3"
                  strokeWidth={1.5}
                />
                <HardHat
                  className="absolute w-14 h-14 sm:w-16 sm:h-16 text-yellow-300 float-icon -top-3 -left-3 sm:-top-4 sm:-left-4"
                  strokeWidth={1.8}
                />
              </div>
            </div>

            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-300 text-xs font-bold uppercase tracking-[0.22em] mb-6 font-body">
              <Construction className="w-3.5 h-3.5" />
              Cantiere aperto
            </div>

            <h1
              data-testid="wip-title"
              className="font-display font-black text-5xl sm:text-7xl tracking-tighter leading-[0.9]"
            >
              <span className="text-white">Work in</span>
              <br />
              <span className="bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-400 bg-clip-text text-transparent">
                Progress.
              </span>
            </h1>

            <p className="text-slate-300 font-body leading-relaxed max-w-2xl mx-auto mt-6 text-base sm:text-lg">
              Stiamo lavorando alla terza sede DMB S.r.l. Qui ci saranno due
              lavaggi automatici di ultima generazione, 4 piste in modalità
              self-service, 8 postazioni di aspirazione con aria compressa,
              distributore per l&apos;acquisto di profumatori e vari pelli.
              Ancora poco e potrai venire a provarla.
            </p>

            {/* Progress bar */}
            <div className="max-w-md mx-auto mt-12">
              <div className="flex items-center justify-between text-xs font-body font-bold uppercase tracking-[0.18em] text-amber-300 mb-3">
                <span>Avanzamento lavori</span>
                <span data-testid="progress-value">{Math.round(progress)}%</span>
              </div>
              <div className="h-3 rounded-full bg-slate-800 overflow-hidden relative">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 transition-all duration-200 ease-out shadow-[0_0_20px_rgba(250,204,21,0.6)]"
                  style={{ width: `${progress}%` }}
                />
                <div className="absolute inset-0 construction-stripes opacity-20" />
              </div>
            </div>

            {/* Mini icons row */}
            <div className="flex items-center justify-center gap-8 mt-12 text-amber-200/70">
              <div className="flex flex-col items-center gap-2">
                <Wrench className="w-6 h-6 float-icon" />
                <span className="text-[10px] uppercase tracking-[0.2em] font-body font-bold">
                  Allestimento
                </span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Cog className="w-6 h-6 rotate-gear" />
                <span className="text-[10px] uppercase tracking-[0.2em] font-body font-bold">
                  Macchinari
                </span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <HardHat className="w-6 h-6 float-icon" style={{ animationDelay: "1s" }} />
                <span className="text-[10px] uppercase tracking-[0.2em] font-body font-bold">
                  Sicurezza
                </span>
              </div>
            </div>

            <Link
              to="/"
              data-testid="wip-back-home"
              className="inline-flex items-center gap-2 mt-14 px-6 py-3 rounded-full bg-white/5 border border-white/10 text-white font-display font-bold text-sm hover:bg-white/10 transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              Torna alle sedi attive
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
};

export default WorkInProgress;
